"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { track, trackPageview } from "@/lib/analytics";

/**
 * Mounted once in the root layout. Fires:
 *  - `pageview` on every route change
 *  - `scroll_depth` (max % per page)
 *  - `time_on_page` on unload / route change
 *  - special `page_register` when /register is visited
 */
export function AnalyticsTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const enteredAtRef = useRef<number>(Date.now());
  const maxScrollRef = useRef<number>(0);
  const lastPathRef = useRef<string>("");

  // Flush time + scroll for the previous page, then fire pageview for the new one.
  useEffect(() => {
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : "");

    if (lastPathRef.current && lastPathRef.current !== url) {
      const secs = Math.max(0, Math.round((Date.now() - enteredAtRef.current) / 1000));
      if (secs > 0 && secs < 3600) {
        track("time_on_page", { path: lastPathRef.current, value: secs });
      }
      if (maxScrollRef.current > 0) {
        track("scroll_depth", { path: lastPathRef.current, value: maxScrollRef.current });
      }
    }

    enteredAtRef.current = Date.now();
    maxScrollRef.current = 0;
    lastPathRef.current = url;

    trackPageview(url);

    if (pathname === "/register") track("page_register", { path: url });
    if (pathname === "/login") track("page_login", { path: url });
    if (pathname?.startsWith("/buy-credits")) track("page_pricing", { path: url });
  }, [pathname, searchParams]);

  // Scroll depth (max %)
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 1;
      const total = Math.max(1, doc.scrollHeight - viewport);
      const pct = Math.min(100, Math.round((scrollTop / total) * 100));
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  // Heartbeat every 30s so we don't lose time if the user closes the tab
  // before the unload handler runs (mobile Safari often skips it).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      track("heartbeat", {
        path: lastPathRef.current,
        value: 30,
      });
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  // Flush on tab hide / unload
  useEffect(() => {
    const flush = () => {
      const secs = Math.max(0, Math.round((Date.now() - enteredAtRef.current) / 1000));
      if (secs > 0 && secs < 3600) {
        track("time_on_page", { path: lastPathRef.current, value: secs });
      }
      if (maxScrollRef.current > 0) {
        track("scroll_depth", { path: lastPathRef.current, value: maxScrollRef.current });
      }
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}
